import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { clearTokenCache } from '@/services/http/httpClient';
import { useTelegramStore } from '@/stores/telegramStore';
import { useTourStore } from '@/stores/tourStore';
import { usePlanStore } from '@/stores/planStore';

interface User {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasCompletedOnboarding: boolean;
  hasSkippedOnboarding: boolean;
  /** Tracks which user the persisted onboarding flags belong to. */
  sessionUserId: string | null;
  _hydrated: boolean;
}

interface AuthActions {
  login: (user: User, token: string) => void;
  loginSimple: () => void;
  logout: () => void;
  setHasCompletedOnboarding: (value: boolean) => void;
  setHasSkippedOnboarding: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  /** Reset onboarding flags when a different user signs in. */
  syncSessionUser: (userId: string) => void;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        hasCompletedOnboarding: false,
        hasSkippedOnboarding: false,
        sessionUserId: null,
        _hydrated: false,

        login: (user, token) => set({ user, token, isAuthenticated: true }, false, 'auth/login'),

        loginSimple: () => set({ isAuthenticated: true }, false, 'auth/loginSimple'),

        logout: () => {
          clearTokenCache();
          // Telegram state is another person's account identity, not just a
          // cache: leaving `linked` and the `@handle` behind would show them to
          // whoever signs in next, and an armed poll would keep reading status
          // for a session that no longer exists.
          useTelegramStore.getState().reset();
          // The plan decides which billing UI shows (e.g. "Upgrade plan"); the
          // next user must not inherit it.
          usePlanStore.getState().reset();
          set(
            {
              user: null,
              token: null,
              isAuthenticated: false,
              hasCompletedOnboarding: false,
              hasSkippedOnboarding: false,
            },
            false,
            'auth/logout'
          );
        },

        setHasCompletedOnboarding: value =>
          set({ hasCompletedOnboarding: value }, false, 'auth/setHasCompletedOnboarding'),

        setHasSkippedOnboarding: value =>
          set({ hasSkippedOnboarding: value }, false, 'auth/setHasSkippedOnboarding'),

        setLoading: value => set({ isLoading: value }, false, 'auth/setLoading'),

        syncSessionUser: userId => {
          if (get().sessionUserId === userId) return;
          // Different user — drop the previous user's Telegram identity for the
          // same reason as the onboarding flags below: none of it is theirs.
          // Done outside the updater so it stays a pure state transition.
          useTelegramStore.getState().reset();
          usePlanStore.getState().reset();
          // Tour state is per browser: drop a tour in progress and credit any
          // legacy "already seen" flag to the user who was signed in.
          useTourStore.getState().switchUser(get().sessionUserId);
          set(
            {
              sessionUserId: userId,
              hasCompletedOnboarding: false,
              hasSkippedOnboarding: false,
            },
            false,
            'auth/syncSessionUser'
          );
        },
      }),
      {
        name: 'lili-auth-storage',
        partialize: state => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          hasCompletedOnboarding: state.hasCompletedOnboarding,
          hasSkippedOnboarding: state.hasSkippedOnboarding,
          sessionUserId: state.sessionUserId,
        }),
        onRehydrateStorage: () => state => {
          if (state) {
            state._hydrated = true;
          } else {
            // First-time users: no persisted data, so state is undefined.
            // Mark hydrated so the app does not stay on PageLoader forever.
            useAuthStore.setState({ _hydrated: true });
          }
        },
      }
    ),
    {
      name: 'AuthStore',
      enabled: import.meta.env.DEV,
    }
  )
);
